import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { getDefaultBrandContext } from '@/lib/brand/approvedHosts';
import type { BrandContext } from '@/lib/brand/types';

export const BRAND_CONTEXT_SCRIPT_ID = '__brand-context';

const BrandContextValue = createContext<BrandContext>(getDefaultBrandContext());

const isBrandContext = (value: Partial<BrandContext> | null | undefined) =>
  Boolean(
    value?.brandKey &&
    value.publicOrigin &&
    value.requestOrigin &&
    value.canonicalOrigin
  );

const readDocumentBrandContext = () => {
  if (typeof document === 'undefined') {
    return null;
  }

  const raw = document.getElementById(BRAND_CONTEXT_SCRIPT_ID)?.textContent;
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as BrandContext;
    return isBrandContext(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const BrandProvider = ({
  brand,
  children,
}: {
  brand?: BrandContext | null;
  children: ReactNode;
}) => {
  const value = useMemo(
    () => brand ?? readDocumentBrandContext() ?? getDefaultBrandContext(),
    [brand]
  );

  return (
    <BrandContextValue.Provider value={value}>
      {children}
    </BrandContextValue.Provider>
  );
};

export const useBrand = () => useContext(BrandContextValue);

// Short catalog name for inline copy. `appName` is the full product name
// ("Nyla Nova STEM Missions"), which is too long to read well mid-sentence.
export const useCatalogName = () =>
  useBrand().brandKey === 'nyla' ? 'Nyla' : 'MindLabs';
